import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { HeaderButton } from './HeroSlice';
import BottomIllustration from './assets/bottom-illustration.svg';
import LeftIllustration from './assets/left-illustration.svg';

import { OverviewLabel } from '@/src/components/OverviewSection';
import { ButtonCloseStyled, Content, VideoOverlay } from '@/src/domains/homepage/styles';
import { transformProperties } from '@/src/domains/prismic/helpers';
import {
  tabletBreakpoint,
  fromTabletBreakpoint,
  fromLargeDesktopBreakpoint,
  fromMobileBreakpoint,
  fromDesktopBreakpoint,
} from '@/src/domains/shared/breakpoints';
import {
  Section,
  OverviewSectionRow,
  OverviewSectionContent,
  OverviewTitleComponent,
  OverviewDescriptionComponent,
  OverviewSectionIllustration,
} from '@/src/views/Services/GlobalRecruitingEmployment/OverviewSlice';

const Index = styled.div`
  font-family: var(--typography-fontBossa);
  color: #f7f3ff;
  ${({ theme }) => theme.typography.scale1};
  position: absolute;
  z-index: -1;
  top: 0;
  ${fluidProp('font-size', '250px', '520px')};

  ${tabletBreakpoint(css`
    ${fluidProp('font-size', '210px', '400px')};
  `)}
`;

export const TitleWrapper = styled(OverviewTitleComponent)`
  && {
    ${({ theme }) => theme.typography.titleXS};

    ${fromTabletBreakpoint(css`
      max-width: 280px;
    `)}
  }
`;

const IconWrapper = styled.div`
  width: 54px;
  height: 54px;
  background-color: var(--primaryColor);
  border-radius: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 26px;
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 70px;
  ${fluidProp('padding-top', '120px', '220px')};
`;

const CtaWrapper = styled.div`
  display: flex;
  justify-content: center;

  ${tabletBreakpoint(css`
    margin-bottom: 100px;
  `)}
`;

const Background = styled.div`
  background: #f7f3ff;
  height: 1800px;
  width: 100%;
  position: absolute;
  clip-path: ellipse(150% 100% at 50% 0%);
`;

const LeftIllustrationWrapper = styled.div`
  position: absolute;
  top: 1650px;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

const BottomIllustrationWrapper = styled(LeftIllustrationWrapper)`
  top: unset;
  bottom: -9%;
`;

const CtaButton = styled(HeaderButton)`
  && {
    ${({ $tone }) => {
      switch ($tone) {
        case 'orange':
          return css`
            color: var(--colors-blank);
            background-color: #ff817b;
          `;

        default:
          return css``;
      }
    }}
  }
`;

export const SectionWrapper = styled(Section)`
  position: relative;
  padding-top: 0;
  padding-bottom: 250px;
  margin-top: -100px;
  z-index: -1;

  ${tabletBreakpoint(css`
    padding-bottom: 100px;
  `)}

  ${OverviewSectionContent} {
    position: relative;
  }

  ${OverviewSectionRow} {
    ${fromTabletBreakpoint(css`
      flex-direction: row-reverse;

      ${Index} {
        left: -10%;
      }

      &:nth-child(even) {
        flex-direction: row;

        ${Index} {
          left: -80%;
        }
      }
    `)}

    &:first-child,
    &:nth-child(2) {
      ${Index} {
        color: var(--colors-blank);
      }
    }

    ${fromTabletBreakpoint(css`
      &:nth-child(2) {
        padding-bottom: 70px;
      }
    `)}
  }

  ${OverviewDescriptionComponent} {
    line-height: 1.6;

    > ul,
    ol {
      list-style: none;
      padding-left: 20px;
      line-height: 2;
      position: relative;

      li {
        &::before {
          content: '';
          background-color: ${({ tone }) => tone};
          width: 8px;
          height: 8px;
          border-radius: 50%;
          position: absolute;
          left: 0;
          margin-top: 13px;
        }
      }
    }
  }

  ${IconWrapper} {
    background-color: ${({ tone }) => tone};
  }

  ${Background} {
    background-color: ${({ secondaryColor }) => secondaryColor};
  }

  ${Index} {
    color: ${({ secondaryColor }) => secondaryColor};
  }

  ${LeftIllustrationWrapper} {
    path {
      fill: ${({ secondaryColor }) => secondaryColor};
    }
  }
`;

const mainColors = {
  purple: '#0061ff',
  orange: '#ff817b',
  red: '#e03d5c',
  green: '#00b568',
};

const secondaryColors = {
  purple: '#F7F3FF',
  orange: 'rgba(223, 239, 211, 0.3)',
  red: 'rgba(224, 61, 92, 0.05)',
  green: 'rgba(223, 239, 211, 0.15)',
};

const Frame = styled.iframe`
  border: none;

  ${fromMobileBreakpoint(css`
    width: 398px;
    height: 300px;
  `)}

  ${fromTabletBreakpoint(css`
    width: 554px;
    height: 450px;
  `)}

  ${fromDesktopBreakpoint(css`
    width: 954px;
    height: 600px;
  `)}

  ${fromLargeDesktopBreakpoint(css`
    width: 1300px;
    height: 700px;
  `)}
`;

export const PlayVideoButton = ({ tone, label, videoId }) => {
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);

  function handleShowVideoDialog(event) {
    event.preventDefault();
    setIsVideoDialogOpen(true);
  }
  function handleDismissVideoDialog(event) {
    event.preventDefault();
    setIsVideoDialogOpen(false);
  }
  return (
    <>
      <CtaButton $tone={tone} onClick={handleShowVideoDialog}>
        {label}
      </CtaButton>
      <VideoOverlay isOpen={isVideoDialogOpen} onDismiss={handleDismissVideoDialog}>
        <Content aria-label="View Remote in Action">
          <ButtonCloseStyled onClick={handleDismissVideoDialog} aria-label="Close dialog" />

          <Frame
            src={`https://player.vimeo.com/video/${videoId}?autoplay=1`}
            allow="autoplay; fullscreen;"
            title="Vimeo video player"
          />
        </Content>
      </VideoOverlay>
    </>
  );
};

export function OverviewSlice({ slice }) {
  const { primary } = transformProperties(slice);

  const {
    howDoesItWorkLabel,
    howDoesItWorkTitle,
    howDoesItWorkDescription,
    ctaLabel,
    ctaLink,
    vimeoVideoId,
  } = primary;
  const { fields } = slice;

  const { context } = slice;
  const { tone } = context;

  return (
    <SectionWrapper
      tone={mainColors[tone] || mainColors.purple}
      secondaryColor={secondaryColors[tone] || secondaryColors.purple}
    >
      <Background />
      <LeftIllustrationWrapper>
        <LeftIllustration />
      </LeftIllustrationWrapper>
      <BottomIllustrationWrapper>
        <BottomIllustration />
      </BottomIllustrationWrapper>
      <ContentContainer>
        <SectionHeader>
          {fieldHasContent(howDoesItWorkLabel) && (
            <OverviewLabel>
              <small>{howDoesItWorkLabel}</small>
            </OverviewLabel>
          )}
          {fieldHasContent(howDoesItWorkTitle) && (
            <OverviewTitleComponent>
              <PrismicRichText field={howDoesItWorkTitle} />
            </OverviewTitleComponent>
          )}
          {fieldHasContent(howDoesItWorkDescription) && (
            <OverviewDescriptionComponent>
              <PrismicRichText field={howDoesItWorkDescription} />
            </OverviewDescriptionComponent>
          )}
        </SectionHeader>
        <div>
          {fields.map(({ icon, title, description, illustration }, index) => (
            <OverviewSectionRow key={index}>
              <OverviewSectionContent>
                <Index>{(index + 1).toString().padStart(2, '0')}</Index>
                {icon && (
                  <IconWrapper>
                    <Image
                      src={icon?.url}
                      alt={icon?.alt || ''}
                      width={icon?.dimensions.width}
                      height={icon?.dimensions.height}
                      quality="100"
                    />
                  </IconWrapper>
                )}
                {fieldHasContent(title) && (
                  <TitleWrapper>
                    <PrismicRichText field={title} />
                  </TitleWrapper>
                )}
                {fieldHasContent(description) && (
                  <OverviewDescriptionComponent>
                    <PrismicRichText field={description} />
                  </OverviewDescriptionComponent>
                )}
              </OverviewSectionContent>
              {illustration && (
                <OverviewSectionIllustration>
                  <Image
                    src={illustration?.url}
                    alt={illustration?.alt || ''}
                    width={illustration?.dimensions.width}
                    height={illustration?.dimensions.height}
                    quality="100"
                  />
                </OverviewSectionIllustration>
              )}
            </OverviewSectionRow>
          ))}
        </div>

        <CtaWrapper>
          {fieldHasContent(vimeoVideoId) ? (
            <PlayVideoButton
              tone={tone}
              label={ctaLabel || 'View Remote in Action'}
              aria-label={ctaLabel || 'View Remote in Action'}
              videoId={vimeoVideoId}
            />
          ) : (
            ctaLink && (
              <CtaButton
                size="lg"
                href={ctaLink?.url}
                target={ctaLink?.target}
                rel="nofollow"
                $tone={tone}
              >
                {ctaLabel}
              </CtaButton>
            )
          )}
        </CtaWrapper>
      </ContentContainer>
    </SectionWrapper>
  );
}
